import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import moment from 'moment'
import { useEffect } from 'react'

import calendarIcon from './assets/calendar.svg'
import alertIcon from './assets/alert.svg'
import './disputes-list.scss'
import { useMutation, useStore } from '../../../utils/mst-hooks'
import { useBottomListListener } from '../../../utils/use-bottom-list-listener'
import {
  DISPUTE_STATUS_DECLINED,
  DISPUTE_STATUS_RAISED,
  DISPUTE_STATUS_RESOLVED,
  DISPUTE_TYPE_OVERTIME,
} from '../../../models/dispute'
import { Header, Separator } from '../../../components/app-layout'

export const DisputesList = observer(() => {
  const { t } = useTranslation()

  const { disputesList, getDisputes, resolveDispute, declineDispute } = useStore().data
  const [, getDisputesListApiCall] = useMutation(({ page }: { page?: number }) => getDisputes({ page }))

  const [, resolveDisputeApiCall] = useMutation(({ id }: { id: number }) => resolveDispute({ id }))
  const [, declineDisputeApiCall] = useMutation(({ id }: { id: number }) => declineDispute({ id }))

  useEffect(() => {
    getDisputesListApiCall({ page: 1 })
  }, [])

  const handleScroll = useBottomListListener(() => getDisputesListApiCall({}))

  return (
    <div className="DisputesList" onScroll={handleScroll}>
      <Header title="Disputes" />
      <Separator.Vertical height={35} />
      <div className="DisputesList__list">
        {disputesList.map(dispute => (
          <div className="DisputesList__disputeItem" key={dispute.id}>
            <div className="DisputesList__disputeItem__top">
              <div className="DisputesList__disputeItem__top__left">
                <div className="DisputesList__disputeItem__top__left__image">
                  <img src={String(dispute.designation_image)} />
                </div>
                <div className="DisputesList__disputeItem__top__left__description">
                  <span className="DisputesList__disputeItem__top__left__description__title">
                    {dispute.designation_name}
                  </span>
                  <span className="DisputesList__disputeItem__top__left__description__subtitle">
                    {dispute.location}
                  </span>
                </div>
              </div>
              <div className="DisputesList__disputeItem__top__right">
                <div className="DisputesList__disputeItem__top__right__label">
                  {dispute.type === DISPUTE_TYPE_OVERTIME ? t('disputes.overtime') : t('disputes.no_show')}
                </div>
              </div>
            </div>
            <div className="DisputesList__disputeItem__info">
              <div className="DisputesList__disputeItem__info__date">
                <img src={calendarIcon} />
                <span className="DisputesList__disputeItem__info__date__value">
                  {moment.unix(dispute.shift_start_time).format('D MMMM YYYY')}
                </span>
              </div>
            </div>
            <div className="DisputesList__disputeItem__body">
              <div className="DisputesList__disputeItem__body__info">
                <img src={String(dispute.sp_image)} />
                <span className="DisputesList__disputeItem__body__info__title">{dispute.sp_name}</span>
              </div>
              <div className="DisputesList__disputeItem__body__attendance">
                <span className="DisputesList__disputeItem__body__attendance__title">
                  {t('disputes.og_attendance')}
                </span>
                <div className="DisputesList__disputeItem__body__attendance__table">
                  <div className="DisputesList__disputeItem__body__attendance__table__value">
                    <span className="DisputesList__disputeItem__body__attendance__table__value__title">
                      {t('disputes.check_in')}
                    </span>
                    <span className="DisputesList__disputeItem__body__attendance__table__value__value">
                      {moment.unix(Number(dispute.sp_checkin_time)).format('h:mmA')}
                    </span>
                  </div>
                  <div className="DisputesList__disputeItem__body__attendance__table__value">
                    <span className="DisputesList__disputeItem__body__attendance__table__value__title">
                      {t('disputes.check_out')}
                    </span>
                    <span className="DisputesList__disputeItem__body__attendance__table__value__value">
                      {moment.unix(Number(dispute.sp_checkout_time)).format('h:mmA')}
                    </span>
                  </div>
                  <div className="DisputesList__disputeItem__body__attendance__table__value">
                    <div className="DisputesList__disputeItem__body__attendance__table__value__title__container">
                      <span className="DisputesList__disputeItem__body__attendance__table__value__title">
                        {t('disputes.total_hours')}
                      </span>
                      <img src={alertIcon} />
                    </div>
                    <span className="DisputesList__disputeItem__body__attendance__table__value__value highlight-red">
                      {t('disputes.n_hours', { count: Number(dispute.sp_total_hours) })}
                    </span>
                  </div>
                </div>
              </div>
              {dispute.type === DISPUTE_TYPE_OVERTIME && (
                <>
                  <Separator.Vertical height={10} />
                  <div className="DisputesList__disputeItem__body__attendance">
                    <span className="DisputesList__disputeItem__body__attendance__title">
                      {t('disputes.your_attendance')}
                    </span>
                    <div className="DisputesList__disputeItem__body__attendance__table">
                      <div className="DisputesList__disputeItem__body__attendance__table__value">
                        <span className="DisputesList__disputeItem__body__attendance__table__value__title">
                          {t('disputes.check_in')}
                        </span>
                        <span className="DisputesList__disputeItem__body__attendance__table__value__value">
                          {moment.unix(Number(dispute.client_checkin_time)).format('h:mmA')}
                        </span>
                      </div>
                      <div className="DisputesList__disputeItem__body__attendance__table__value">
                        <span className="DisputesList__disputeItem__body__attendance__table__value__title">
                          {t('disputes.check_out')}
                        </span>
                        <span className="DisputesList__disputeItem__body__attendance__table__value__value">
                          {moment.unix(Number(dispute.client_checkout_time)).format('h:mmA')}
                        </span>
                      </div>
                      <div className="DisputesList__disputeItem__body__attendance__table__value">
                        <div className="DisputesList__disputeItem__body__attendance__table__value__title__container">
                          <span className="DisputesList__disputeItem__body__attendance__table__value__title">
                            {t('disputes.total_hours')}
                          </span>
                          <img src={alertIcon} />
                        </div>
                        <span className="DisputesList__disputeItem__body__attendance__table__value__value highlight-red">
                          {t('disputes.n_hours', { count: Number(dispute.client_total_hours) })}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
              <Separator.Vertical height={5} />
              <div className="DisputesList__disputeItem__body__alert">
                <img src={alertIcon} />
                <div className="DisputesList__disputeItem__body__alert__content">
                  {dispute.type === DISPUTE_TYPE_OVERTIME ? (
                    <span className="DisputesList__disputeItem__body__alert__text">
                      {t('disputes.og_claims_n_hours_more', {
                        count: Number(dispute.sp_total_hours) - Number(dispute.client_total_hours),
                      })}
                    </span>
                  ) : (
                    <span className="DisputesList__disputeItem__body__alert__text">
                      {t('disputes.og_claims_show_up')}
                    </span>
                  )}
                  {dispute.sp_comment && (
                    <span className="DisputesList__disputeItem__body__alert__comment">
                      <span className="DisputesList__disputeItem__body__alert__comment__label">
                        {t('disputes.og_comment')}:
                      </span>{' '}
                      {dispute.sp_comment}
                    </span>
                  )}
                  {dispute.sp_file && (
                    <a
                      href={dispute.sp_file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="DisputesList__disputeItem__body__alert__file"
                    >
                      {t('disputes.view_attached_file')}
                    </a>
                  )}
                </div>
              </div>
            </div>
            {dispute.status === DISPUTE_STATUS_RAISED && (
              <div className="DisputesList__disputeItem__actions">
                <button
                  onClick={() => {
                    if (confirm(t('disputes.are_you_sure_to_agree'))) {
                      resolveDisputeApiCall({ id: dispute.id })
                      toast.success(t('disputes.dispute_resolved'))
                    }
                  }}
                  className="DisputesList__disputeItem__actions__confirm"
                >
                  {t('disputes.i_agree')}
                </button>
                <button
                  onClick={() => {
                    if (confirm(t('disputes.are_you_sure_to_decline'))) {
                      declineDisputeApiCall({ id: dispute.id })
                      toast.success(t('disputes.dispute_declined'))
                    }
                  }}
                  className="DisputesList__disputeItem__actions__reject"
                >
                  {t('disputes.i_dont_agree')}
                </button>
              </div>
            )}
            {dispute.status === DISPUTE_STATUS_RESOLVED && (
              <div className="DisputesList__disputeItem__actions">
                <button className="DisputesList__disputeItem__actions__confirm button-disabled" disabled>
                  {t('disputes.i_agree')}
                </button>
              </div>
            )}
            {dispute.status === DISPUTE_STATUS_DECLINED && (
              <div className="DisputesList__disputeItem__actions">
                <button className="DisputesList__disputeItem__actions__reject">{t('disputes.i_dont_agree')}</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
})
